/**
 * MattersTab
 *
 * Fariha's bi-monthly report: "matters with no/low time entered (2 hours or
 * less) once opened and usually for the previous month."
 *
 * Period and threshold are both controls rather than constants — "2 hours" and
 * "last month" are her current defaults, not fixed rules. Last Month is already
 * a date preset, so it needs no checkbox of its own.
 *
 * No matter open date exists in the schema (see types/reports.ts), so staleness
 * is measured from the last date any time was entered.
 */

import { Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
	DATE_PRESET_LABELS,
	type DatePreset,
	getDateRange,
} from "@/features/attorney/hooks/useReportingDashboard";
import { cn } from "@/shared/lib/utils";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { Button } from "@/v2/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import { useUnbilledMatters } from "../../hooks/useReportsData";
import type { UnbilledMatterRow } from "../../types/reports";
import MatterStalenessChart from "./MatterStalenessChart";
import ReportKpiRow, { type KpiCardSpec } from "./ReportKpiRow";
import ReportTable, { formatHours, type ReportColumn } from "./ReportTable";

const THRESHOLD_OPTIONS = [0, 1, 2, 5, 10];
/**
 * Rows rendered before "Show more". The table is not virtualized, so every row
 * is real DOM — a few hundred matters × six cells is a visible stall.
 */
const PAGE_SIZE = 100;

function thresholdLabel(hours: number): string {
	return hours === 0 ? "No time at all" : `${hours} hours or less`;
}

const columns: Array<ReportColumn<UnbilledMatterRow>> = [
	{
		key: "number",
		header: "Matter",
		width: "10rem",
		cell: (r) => r.matterNumber,
	},
	{ key: "desc", header: "Description", cell: (r) => r.matterDescription },
	{ key: "client", header: "Client", cell: (r) => r.client },
	{
		key: "status",
		header: "Status",
		width: "8rem",
		cell: (r) => r.status ?? <span className="text-muted-foreground">—</span>,
	},
	{
		key: "hours",
		header: "Hours in period",
		align: "right",
		width: "8rem",
		cell: (r) => formatHours(r.hours),
	},
	{
		key: "lifetime",
		header: "Lifetime hours",
		align: "right",
		width: "8rem",
		cell: (r) => formatHours(r.lifetimeHours),
	},
	{
		key: "first",
		header: "Work started",
		align: "right",
		width: "8rem",
		cell: (r) =>
			r.firstActivity ? (
				new Date(`${r.firstActivity}T00:00:00`).toLocaleDateString(undefined, {
					month: "short",
					day: "numeric",
					year: "numeric",
				})
			) : (
				<span className="text-muted-foreground">Never</span>
			),
	},
	{
		key: "stale",
		header: "Last worked",
		align: "right",
		width: "8rem",
		cell: (r) =>
			r.daysStale === null ? (
				<span className="text-muted-foreground">Never</span>
			) : (
				`${r.daysStale}d ago`
			),
	},
];

/** Previous calendar month — the report's usual window. */
function previousMonth(): { from: Date; to: Date } {
	const now = new Date();
	return {
		from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
		to: new Date(now.getFullYear(), now.getMonth(), 0),
	};
}

export default function MattersTab() {
	const [dateRange, setDateRange] = useState(previousMonth);
	const [threshold, setThreshold] = useState(2);
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
	// null is implicit "all statuses" — the tenant's status values are unknown
	// until the first response, so nothing can be excluded by default.
	const [statuses, setStatuses] = useState<string[] | null>(null);

	const { data, loading, error } = useUnbilledMatters(
		dateRange.from,
		dateRange.to,
		threshold,
		statuses,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset paging when the result set changes.
	useEffect(() => {
		setVisibleCount(PAGE_SIZE);
	}, [dateRange, threshold, statuses]);

	const activePreset = useMemo(() => {
		const keys = Object.keys(DATE_PRESET_LABELS) as DatePreset[];
		return keys.find((key) => {
			const r = getDateRange(key);
			return (
				r.from.toDateString() === dateRange.from.toDateString() &&
				r.to.toDateString() === dateRange.to.toDateString()
			);
		});
	}, [dateRange]);

	const cards: KpiCardSpec[] = data
		? [
				{
					label: thresholdLabel(threshold),
					value: String(data.underThresholdCount),
					gradient: "green",
				},
				{
					label: "Hours in period",
					value: formatHours(data.rows.reduce((sum, r) => sum + r.hours, 0)),
					suffix: "hrs",
					gradient: "teal",
				},
				{
					label: "Matters worked",
					value: String(data.totalMatters),
					gradient: "dark",
				},
			]
		: [];

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-wrap items-end gap-3">
				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted-foreground">
						Period
					</span>
					<DatePicker
						className="w-fit"
						dateRange={dateRange}
						onDateChange={(day) => setDateRange({ from: day, to: day })}
						onDateRangeChange={(range) =>
							setDateRange({ from: range.from, to: range.to })
						}
						selectionMode="range"
						sideSlot={({ selectRange }) => (
							<div className="flex w-[160px] flex-col border-r p-1">
								{Object.entries(DATE_PRESET_LABELS).map(([key, label]) => {
									const range = getDateRange(key as DatePreset);
									return (
										<button
											key={key}
											type="button"
											className={cn(
												"flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted",
												activePreset === key && "bg-muted font-medium",
											)}
											onClick={() => selectRange(range)}
										>
											<Check
												className={cn(
													"h-3.5 w-3.5 shrink-0",
													activePreset === key ? "opacity-100" : "opacity-0",
												)}
											/>
											{label}
										</button>
									);
								})}
							</div>
						)}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted-foreground">
						Threshold
					</span>
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className="w-56 justify-start font-normal"
							>
								{thresholdLabel(threshold)}
							</Button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-56 p-1">
							{THRESHOLD_OPTIONS.map((t) => (
								<button
									key={t}
									type="button"
									onClick={() => setThreshold(t)}
									className={cn(
										"flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted",
										threshold === t && "bg-muted font-medium",
									)}
								>
									<Check
										className={cn(
											"h-3.5 w-3.5 shrink-0",
											threshold === t ? "opacity-100" : "opacity-0",
										)}
									/>
									{thresholdLabel(t)}
								</button>
							))}
						</PopoverContent>
					</Popover>
				</div>

				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted-foreground">
						Matter status
					</span>
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className="w-56 justify-start font-normal"
							>
								{statuses === null
									? `All statuses (${data?.availableStatuses.length ?? 0})`
									: statuses.length === 0
										? "None selected"
										: `${statuses.length} selected`}
							</Button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-56 p-1">
							<button
								type="button"
								onClick={() => setStatuses(null)}
								className={cn(
									"flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted",
									statuses === null && "bg-muted font-medium",
								)}
							>
								<Check
									className={cn(
										"h-3.5 w-3.5 shrink-0",
										statuses === null ? "opacity-100" : "opacity-0",
									)}
								/>
								All statuses
							</button>
							{(data?.availableStatuses ?? []).map((st) => {
								const on = statuses === null || statuses.includes(st);
								return (
									<button
										key={st}
										type="button"
										onClick={() =>
											setStatuses((prev) => {
												const base = prev ?? data?.availableStatuses ?? [];
												return base.includes(st)
													? base.filter((v) => v !== st)
													: [...base, st];
											})
										}
										className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
									>
										<Check
											className={cn(
												"h-3.5 w-3.5 shrink-0",
												on ? "opacity-100" : "opacity-0",
											)}
										/>
										{st}
									</button>
								);
							})}
						</PopoverContent>
					</Popover>
				</div>
			</div>

			{error && (
				<div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
					{error}
				</div>
			)}

			{loading && !data && (
				<div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
					Loading…
				</div>
			)}

			{data && (
				<>
					<ReportKpiRow cards={cards} />

					{data.rows.length > 0 && (
						<div className="rounded-lg border border-border p-4">
							<div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
								<h2 className="text-sm font-medium text-foreground">
									Time since last worked
								</h2>
								<p className="text-xs text-muted-foreground">
									{data.rows.length.toLocaleString()} matters{" "}
									{thresholdLabel(threshold).toLowerCase()}
								</p>
							</div>
							<p className="mb-3 text-xs text-muted-foreground">
								How long ago each of these matters last had time entered — a
								quiet month reads differently on a matter worked last week than
								on one untouched for a year.
							</p>
							<MatterStalenessChart rows={data.rows} />
						</div>
					)}

					<ReportTable
						columns={columns}
						rows={data.rows.slice(0, visibleCount)}
						rowKey={(r) => r.matterId}
						isStale={loading}
						emptyMessage={`No matters ${thresholdLabel(threshold).toLowerCase()} in this period.`}
					/>

					{data.rows.length > visibleCount && (
						<div className="flex flex-col items-center gap-1">
							<Button
								variant="outline"
								onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
							>
								Show {Math.min(PAGE_SIZE, data.rows.length - visibleCount)} more
							</Button>
							<p className="text-xs text-muted-foreground">
								Showing {visibleCount} of {data.rows.length} matters
							</p>
						</div>
					)}
				</>
			)}
		</div>
	);
}
